import {
  type Abschnitt,
  type Feld,
  type FormSchema,
  type SichtbarWenn,
  REINE_ANZEIGE_TYPEN,
} from './schemas/form-schema.js';
import type { ProtokollData } from './schemas/protokoll.js';
import { mangelSchema, schluesselZeileSchema } from './schemas/protokoll-daten.js';
import { parseDeutscheZahl } from './format.js';

/**
 * Prüft Protokolldaten gegen das `formSchema` der Template-Version.
 *
 * Dieselbe Funktion läuft im Browser (Vollständigkeitsprüfung vor der
 * Unterschrift) und in der Lambda (Prüfung beim Sync) — die Regeln können
 * nicht auseinanderlaufen.
 */

export interface Befund {
  /** `abschnittId.feldId` bzw. `abschnittId[3].feldId` bei Wiederholungen. */
  pfad: string;
  abschnittId: string;
  abschnittTitel: string;
  feldId?: string;
  feldLabel?: string;
  /** Zeilennummer (1-basiert) in einem wiederholbaren Abschnitt. */
  zeile?: number;
  meldung: string;
  schwere: 'fehler' | 'hinweis';
}

const istLeer = (wert: unknown): boolean => {
  if (wert === null || wert === undefined) return true;
  if (typeof wert === 'string') return wert.trim() === '';
  if (Array.isArray(wert)) return wert.length === 0;
  if (typeof wert === 'object') return Object.keys(wert as object).length === 0;
  return false;
};

const alsZahl = (wert: unknown): number | null => {
  if (typeof wert === 'number') return Number.isFinite(wert) ? wert : null;
  if (typeof wert === 'string') return parseDeutscheZahl(wert);
  return null;
};

/**
 * Wertet eine `sichtbarWenn`-Bedingung gegen die Werte desselben
 * Abschnitts (bzw. derselben Wiederholungszeile) aus.
 */
export const istSichtbar = (
  bedingung: SichtbarWenn | undefined,
  werte: Record<string, unknown>,
): boolean => {
  if (!bedingung) return true;
  const wert = werte[bedingung.feld];

  switch (bedingung.operator) {
    case 'gesetzt':
      return !istLeer(wert);
    case 'leer':
      return istLeer(wert);
    case 'gleich':
      if (typeof wert === 'boolean' || typeof bedingung.wert === 'boolean') {
        return Boolean(wert) === Boolean(bedingung.wert);
      }
      return String(wert ?? '') === String(bedingung.wert ?? '');
    case 'ungleich':
      if (typeof wert === 'boolean' || typeof bedingung.wert === 'boolean') {
        return Boolean(wert) !== Boolean(bedingung.wert);
      }
      return String(wert ?? '') !== String(bedingung.wert ?? '');
    case 'enthaelt': {
      const suchwert = String(bedingung.wert ?? '');
      if (Array.isArray(wert)) return wert.map(String).includes(suchwert);
      return String(wert ?? '').includes(suchwert);
    }
    case 'groesser': {
      const a = alsZahl(wert);
      const b = alsZahl(bedingung.wert);
      return a !== null && b !== null && a > b;
    }
    case 'kleiner': {
      const a = alsZahl(wert);
      const b = alsZahl(bedingung.wert);
      return a !== null && b !== null && a < b;
    }
    default:
      return true;
  }
};

const feldLabel = (feld: Feld): string => ('label' in feld && feld.label) || feld.id;

const pruefeFeldWert = (
  feld: Feld,
  wert: unknown,
  basis: Omit<Befund, 'meldung' | 'schwere' | 'pfad'>,
  pfad: string,
): Befund[] => {
  const befunde: Befund[] = [];
  const fehler = (meldung: string, schwere: Befund['schwere'] = 'fehler'): void => {
    befunde.push({ ...basis, pfad, meldung, schwere });
  };

  if (REINE_ANZEIGE_TYPEN.includes(feld.typ)) return befunde;
  if (feld.typ === 'signaturblock') return befunde;

  const leer = istLeer(wert);

  if (feld.pflicht && leer) {
    fehler(`„${feldLabel(feld)}" ist ein Pflichtfeld und noch leer.`);
    return befunde;
  }
  if (leer) return befunde;

  switch (feld.typ) {
    case 'text':
    case 'mehrzeilig': {
      if (typeof wert !== 'string') {
        fehler(`„${feldLabel(feld)}" muss ein Text sein.`);
        break;
      }
      if (feld.maxLaenge !== undefined && wert.length > feld.maxLaenge) {
        fehler(`„${feldLabel(feld)}" darf höchstens ${feld.maxLaenge} Zeichen lang sein.`);
      }
      if (feld.typ === 'text' && feld.muster) {
        try {
          if (!new RegExp(feld.muster).test(wert)) {
            fehler(`„${feldLabel(feld)}" hat nicht das erwartete Format.`);
          }
        } catch {
          fehler(`Das Prüfmuster von „${feldLabel(feld)}" ist ungültig.`, 'hinweis');
        }
      }
      break;
    }
    case 'zahl':
    case 'waehrung': {
      const zahl = alsZahl(wert);
      if (zahl === null) {
        fehler(`„${feldLabel(feld)}" muss eine Zahl sein.`);
        break;
      }
      if (feld.min !== undefined && zahl < feld.min) {
        fehler(`„${feldLabel(feld)}" darf nicht kleiner als ${feld.min} sein.`);
      }
      if (feld.max !== undefined && zahl > feld.max) {
        fehler(`„${feldLabel(feld)}" darf nicht größer als ${feld.max} sein.`);
      }
      break;
    }
    case 'datum': {
      if (typeof wert !== 'string' || Number.isNaN(new Date(wert).getTime())) {
        fehler(`„${feldLabel(feld)}" enthält kein gültiges Datum.`);
      }
      break;
    }
    case 'uhrzeit': {
      if (typeof wert !== 'string' || !/^\d{1,2}:\d{2}$/.test(wert)) {
        fehler(`„${feldLabel(feld)}" muss als HH:MM angegeben werden.`);
      }
      break;
    }
    case 'auswahl':
    case 'zustandsskala': {
      const optionen = feld.typ === 'auswahl' ? feld.optionen : (feld.stufen ?? []);
      const andereErlaubt = feld.typ === 'auswahl' ? feld.andereErlaubt === true : false;
      if (
        optionen.length > 0 &&
        !andereErlaubt &&
        typeof wert === 'string' &&
        !optionen.includes(wert)
      ) {
        fehler(`„${wert}" ist bei „${feldLabel(feld)}" keine zulässige Auswahl.`);
      }
      break;
    }
    case 'mehrfachauswahl': {
      if (!Array.isArray(wert)) {
        fehler(`„${feldLabel(feld)}" muss eine Liste sein.`);
        break;
      }
      const unbekannt = wert.filter((eintrag) => !feld.optionen.includes(String(eintrag)));
      if (unbekannt.length > 0) {
        fehler(`Unzulässige Auswahl bei „${feldLabel(feld)}": ${unbekannt.join(', ')}.`);
      }
      if (feld.minAuswahl !== undefined && wert.length < feld.minAuswahl) {
        fehler(
          `Bei „${feldLabel(feld)}" müssen mindestens ${feld.minAuswahl} Einträge gewählt sein.`,
        );
      }
      if (feld.maxAuswahl !== undefined && wert.length > feld.maxAuswahl) {
        fehler(
          `Bei „${feldLabel(feld)}" dürfen höchstens ${feld.maxAuswahl} Einträge gewählt sein.`,
        );
      }
      break;
    }
    case 'jaNein': {
      if (typeof wert !== 'boolean') {
        fehler(`„${feldLabel(feld)}" muss Ja oder Nein sein.`);
      }
      break;
    }
    case 'zaehlerstand': {
      if (typeof wert !== 'object' || wert === null || Array.isArray(wert)) {
        fehler(`„${feldLabel(feld)}" hat ein unerwartetes Format.`);
        break;
      }
      const stand = alsZahl((wert as Record<string, unknown>).stand);
      if (stand === null) {
        fehler(`Bei „${feldLabel(feld)}" fehlt ein gültiger Zählerstand.`);
      } else if (stand < 0) {
        fehler(`Der Zählerstand bei „${feldLabel(feld)}" kann nicht negativ sein.`);
      }
      break;
    }
    case 'fotos': {
      if (!Array.isArray(wert)) {
        fehler(`„${feldLabel(feld)}" muss eine Fotoliste sein.`);
        break;
      }
      if (feld.min !== undefined && wert.length < feld.min) {
        fehler(`Bei „${feldLabel(feld)}" werden mindestens ${feld.min} Fotos erwartet.`);
      }
      if (feld.max !== undefined && wert.length > feld.max) {
        fehler(`Bei „${feldLabel(feld)}" sind höchstens ${feld.max} Fotos erlaubt.`);
      }
      break;
    }
    case 'maengelblock': {
      if (!Array.isArray(wert)) {
        fehler(`„${feldLabel(feld)}" muss eine Mängelliste sein.`);
        break;
      }
      wert.forEach((eintrag, index) => {
        const geprueft = mangelSchema.safeParse(eintrag);
        if (!geprueft.success) {
          const grund = geprueft.error.issues[0]?.message ?? 'Ungültiger Eintrag';
          fehler(`Mangel ${index + 1} bei „${feldLabel(feld)}": ${grund}`);
        }
      });
      break;
    }
    case 'schluesseltabelle': {
      if (!Array.isArray(wert)) {
        fehler(`„${feldLabel(feld)}" muss eine Schlüsselliste sein.`);
        break;
      }
      wert.forEach((eintrag, index) => {
        const geprueft = schluesselZeileSchema.safeParse(eintrag);
        if (!geprueft.success) {
          const grund = geprueft.error.issues[0]?.message ?? 'Ungültiger Eintrag';
          fehler(`Schlüsselzeile ${index + 1}: ${grund}`);
        }
      });
      break;
    }
    case 'wiederholbar': {
      if (!Array.isArray(wert)) {
        fehler(`„${feldLabel(feld)}" muss eine Liste sein.`);
        break;
      }
      if (feld.min !== undefined && wert.length < feld.min) {
        fehler(`„${feldLabel(feld)}" braucht mindestens ${feld.min} Einträge.`);
      }
      if (feld.max !== undefined && wert.length > feld.max) {
        fehler(`„${feldLabel(feld)}" darf höchstens ${feld.max} Einträge haben.`);
      }
      wert.forEach((zeile, index) => {
        const zeilenWerte = (zeile ?? {}) as Record<string, unknown>;
        for (const unterfeld of feld.felder) {
          if (!istSichtbar(unterfeld.sichtbarWenn, zeilenWerte)) continue;
          befunde.push(
            ...pruefeFeldWert(
              unterfeld,
              zeilenWerte[unterfeld.id],
              { ...basis, feldId: unterfeld.id, feldLabel: feldLabel(unterfeld), zeile: index + 1 },
              `${pfad}[${index}].${unterfeld.id}`,
            ),
          );
        }
      });
      break;
    }
    default:
      break;
  }

  return befunde;
};

const pruefeAbschnitt = (abschnitt: Abschnitt, data: ProtokollData): Befund[] => {
  const typ = abschnitt.typ ?? 'standard';
  const rohwert = data[abschnitt.id];
  const basis = {
    abschnittId: abschnitt.id,
    abschnittTitel: abschnitt.titel,
  };

  if (typ === 'signaturblock') return [];

  if (typ === 'schluesseltabelle') {
    const befunde: Befund[] = [];
    const zeilen = Array.isArray(rohwert) ? rohwert : [];
    zeilen.forEach((zeile, index) => {
      const geprueft = schluesselZeileSchema.safeParse(zeile);
      if (!geprueft.success) {
        befunde.push({
          ...basis,
          pfad: `${abschnitt.id}[${index}]`,
          zeile: index + 1,
          meldung: `Schlüsselzeile ${index + 1}: ${geprueft.error.issues[0]?.message ?? 'ungültig'}`,
          schwere: 'fehler',
        });
      }
    });
    return befunde;
  }

  if (typ === 'wiederholbar') {
    const befunde: Befund[] = [];
    const zeilen = Array.isArray(rohwert) ? rohwert : [];
    if (abschnitt.min !== undefined && zeilen.length < abschnitt.min) {
      befunde.push({
        ...basis,
        pfad: abschnitt.id,
        meldung: `„${abschnitt.titel}" braucht mindestens ${abschnitt.min} Einträge.`,
        schwere: 'fehler',
      });
    }
    zeilen.forEach((zeile, index) => {
      const zeilenWerte = (zeile ?? {}) as Record<string, unknown>;
      for (const feld of abschnitt.felder ?? []) {
        if (!istSichtbar(feld.sichtbarWenn, zeilenWerte)) continue;
        befunde.push(
          ...pruefeFeldWert(
            feld,
            zeilenWerte[feld.id],
            { ...basis, feldId: feld.id, feldLabel: feldLabel(feld), zeile: index + 1 },
            `${abschnitt.id}[${index}].${feld.id}`,
          ),
        );
      }
    });
    return befunde;
  }

  const werte = (rohwert ?? {}) as Record<string, unknown>;
  const befunde: Befund[] = [];
  for (const feld of abschnitt.felder ?? []) {
    if (!istSichtbar(feld.sichtbarWenn, werte)) continue;
    befunde.push(
      ...pruefeFeldWert(
        feld,
        werte[feld.id],
        { ...basis, feldId: feld.id, feldLabel: feldLabel(feld) },
        `${abschnitt.id}.${feld.id}`,
      ),
    );
  }
  return befunde;
};

export interface Pruefergebnis {
  ok: boolean;
  befunde: Befund[];
  fehler: Befund[];
  hinweise: Befund[];
}

/** Prüft die gesamten Protokolldaten gegen das Formularschema. */
export const pruefeProtokollDaten = (schema: FormSchema, data: ProtokollData): Pruefergebnis => {
  const befunde: Befund[] = [];
  for (const abschnitt of schema.abschnitte) {
    if (!istSichtbar(abschnitt.sichtbarWenn, (data ?? {}) as Record<string, unknown>)) continue;
    befunde.push(...pruefeAbschnitt(abschnitt, data ?? {}));
  }
  const fehler = befunde.filter((befund) => befund.schwere === 'fehler');
  const hinweise = befunde.filter((befund) => befund.schwere === 'hinweis');
  return { ok: fehler.length === 0, befunde, fehler, hinweise };
};

/** Anteil ausgefüllter Pflichtfelder — Grundlage der Fortschrittsanzeige. */
export const fortschritt = (schema: FormSchema, data: ProtokollData): number => {
  let pflicht = 0;
  let erfuellt = 0;

  for (const abschnitt of schema.abschnitte) {
    const typ = abschnitt.typ ?? 'standard';
    if (typ === 'signaturblock') continue;
    const rohwert = data?.[abschnitt.id];

    if (typ === 'wiederholbar') {
      const zeilen = Array.isArray(rohwert) ? rohwert : [];
      for (const zeile of zeilen) {
        const werte = (zeile ?? {}) as Record<string, unknown>;
        for (const feld of abschnitt.felder ?? []) {
          if (!feld.pflicht || !istSichtbar(feld.sichtbarWenn, werte)) continue;
          pflicht += 1;
          if (!istLeer(werte[feld.id])) erfuellt += 1;
        }
      }
      continue;
    }

    const werte = (rohwert ?? {}) as Record<string, unknown>;
    for (const feld of abschnitt.felder ?? []) {
      if (!feld.pflicht || !istSichtbar(feld.sichtbarWenn, werte)) continue;
      pflicht += 1;
      if (!istLeer(werte[feld.id])) erfuellt += 1;
    }
  }

  if (pflicht === 0) return 1;
  return erfuellt / pflicht;
};
